import { useRef } from "react";
import { FileText, X } from "lucide-react";
import type { CompanyDocumentKey, DriverDocumentKey } from "../../types/vtc";
import { DOCUMENT_ACCEPT } from "../../data/auth";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";

interface DocumentUploadRowProps {
  docKey: CompanyDocumentKey | DriverDocumentKey;
  file?: File;
  error?: string;
  onSelect: (file: File) => void;
  onRemove: () => void;
}

export default function DocumentUploadRow({
  docKey,
  file,
  error,
  onSelect,
  onRemove,
}: DocumentUploadRowProps) {
  const t = useT();
  const docs = t.auth.documents;
  const spec = docs[docKey];
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-3 rounded-card border px-4 py-3.5",
          error ? "border-tone-red-fg" : "border-line-strong",
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-ink">{spec.label}</div>
          {file ? (
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-tone-green-fg">
              <FileText size={13} className="shrink-0" />
              <span className="truncate">{file.name}</span>
            </div>
          ) : (
            <div className="mt-0.5 text-xs text-muted">{spec.hint}</div>
          )}
        </div>

        {file && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={docs.remove(spec.label)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-muted hover:bg-surface hover:text-ink"
          >
            <X size={16} />
          </button>
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="h-9 shrink-0 cursor-pointer rounded border-0 bg-surface px-3.5 text-[13px] font-medium text-ink transition-colors hover:bg-surface-hover"
        >
          {file ? docs.replace : docs.upload}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={DOCUMENT_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const picked = e.target.files?.[0];
            if (picked) onSelect(picked);
            // Allow picking the same file again after removing it.
            e.target.value = "";
          }}
        />
      </div>
      {error && <p className="mt-1 mb-0 text-xs text-tone-red-fg">{error}</p>}
    </div>
  );
}
